import React, { useMemo, useState } from 'react'
import { createRoot } from 'react-dom/client'

import { csrfToken } from '../utils/csrfToken'
import { formatMoney, MoneyInput } from './MoneyInput'

function emptyMilestone(statuses) {
  return {
    name: '',
    target_start_date: '',
    target_end_date: '',
    status: statuses[0] || 'planned',
    price: '',
  }
}

function ProjectForm({ formAction, clients, users, milestoneStatuses, initialErrors }) {
  const initialClientId = useMemo(() => {
    return new URLSearchParams(window.location.search).get('client_id') || ''
  }, [])

  const [form, setForm] = useState({
    name: '',
    description: '',
    client_id: initialClientId,
    project_sponsor_id: '',
    project_lead_id: '',
  })
  const [milestones, setMilestones] = useState([emptyMilestone(milestoneStatuses)])
  const [errors, setErrors] = useState(initialErrors || [])
  const [submitting, setSubmitting] = useState(false)

  const totalPrice = useMemo(() => {
    return milestones.reduce((sum, milestone) => sum + Number(milestone.price || 0), 0)
  }, [milestones])

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function updateMilestone(index, key, value) {
    setMilestones((prev) => prev.map((item, idx) => (idx === index ? { ...item, [key]: value } : item)))
  }

  function addMilestone() {
    setMilestones((prev) => [...prev, emptyMilestone(milestoneStatuses)])
  }

  function removeMilestone(index) {
    setMilestones((prev) => prev.filter((_, idx) => idx !== index))
  }

  async function submit(event) {
    event.preventDefault()
    setErrors([])
    setSubmitting(true)

    const filteredMilestones = milestones.filter((milestone) => {
      return [milestone.name, milestone.target_start_date, milestone.target_end_date, milestone.price]
        .some((value) => String(value || '').trim().length > 0)
    })

    const payload = {
      project: {
        ...form,
        milestones_attributes: filteredMilestones.map((milestone) => ({
          ...milestone,
          price: milestone.price === '' ? 0 : milestone.price,
        })),
      },
    }

    try {
      const response = await fetch(formAction, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken() || '',
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => null)
        setErrors(data?.errors || ['Failed to create project.'])
        return
      }

      const data = await response.json()
      if (data?.redirect_url) {
        window.location.href = data.redirect_url
      }
    } catch (error) {
      setErrors([error.message || 'Failed to create project.'])
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit}>
      <div className="header mt-md-5">
        <div className="header-body">
          <div className="row align-items-center">
            <div className="col">
              <h6 className="header-pretitle">Overview</h6>
              <h1 className="header-title">New Project</h1>
            </div>
          </div>
        </div>
      </div>

      {errors.length ? (
        <div className="alert alert-danger">
          <ul className="mb-0">
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="card">
        <div className="card-body">
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label className="form-label" htmlFor="project-name">Project name</label>
                <input
                  id="project-name"
                  className="form-control"
                  value={form.name}
                  onChange={(event) => updateField('name', event.target.value)}
                  required
                />
                <small className="form-text text-body-secondary">Use a clear, client-facing project name.</small>
              </div>
            </div>
            <div className="col-12">
              <div className="form-group">
                <label className="form-label" htmlFor="project-description">Description</label>
                <textarea
                  id="project-description"
                  className="form-control"
                  rows="4"
                  value={form.description}
                  onChange={(event) => updateField('description', event.target.value)}
                  required
                />
                <small className="form-text text-body-secondary">Summarize scope and key outcomes.</small>
              </div>
            </div>
            <div className="col-12">
              <div className="form-group">
                <label className="form-label" htmlFor="project-client">Client</label>
                <select
                  id="project-client"
                  className="form-select"
                  value={form.client_id}
                  onChange={(event) => updateField('client_id', event.target.value)}
                  required
                >
                  <option value="">Select client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
                <small className="form-text text-body-secondary">Select the client account tied to this work.</small>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="form-group">
                <label className="form-label" htmlFor="project-sponsor">Project sponsor</label>
                <select
                  id="project-sponsor"
                  className="form-select"
                  value={form.project_sponsor_id}
                  onChange={(event) => updateField('project_sponsor_id', event.target.value)}
                  required
                >
                  <option value="">Select sponsor</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </select>
                <small className="form-text text-body-secondary">Owner of budget and overall success.</small>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="form-group">
                <label className="form-label" htmlFor="project-lead">Project lead</label>
                <select
                  id="project-lead"
                  className="form-select"
                  value={form.project_lead_id}
                  onChange={(event) => updateField('project_lead_id', event.target.value)}
                  required
                >
                  <option value="">Select lead</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </select>
                <small className="form-text text-body-secondary">Primary delivery owner for this project.</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-header d-flex align-items-center">
          <h4 className="card-header-title mb-0">Milestones</h4>
          <button type="button" className="btn btn-sm btn-primary ms-auto" onClick={addMilestone}>
            Add milestone
          </button>
        </div>
        <div className="px-3 pt-2 text-body-secondary small">Add key checkpoints with dates and budget to track progress.</div>
        <div className="table-responsive mb-0">
          <table className="table table-sm table-nowrap card-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Target start date</th>
                <th>Target end date</th>
                <th>Price</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {milestones.map((milestone, index) => (
                <tr key={`milestone-${index}`}>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={milestone.name}
                      onChange={(event) => updateMilestone(index, 'name', event.target.value)}
                      placeholder="Milestone name"
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={milestone.target_start_date}
                      onChange={(event) => updateMilestone(index, 'target_start_date', event.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={milestone.target_end_date}
                      onChange={(event) => updateMilestone(index, 'target_end_date', event.target.value)}
                    />
                  </td>
                  <td>
                    <MoneyInput
                      inputClassName="form-control form-control-sm"
                      value={milestone.price}
                      onChange={(value) => updateMilestone(index, 'price', value)}
                      placeholder="PHP 0"
                    />
                  </td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={milestone.status}
                      onChange={(event) => updateMilestone(index, 'status', event.target.value)}
                    >
                      {milestoneStatuses.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </td>
                  <td className="text-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-white"
                      onClick={() => removeMilestone(index)}
                      disabled={milestones.length <= 1}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card-footer d-flex align-items-center justify-content-between">
          <div className="text-body-secondary">Total: {formatMoney(totalPrice)}</div>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            Create Project
          </button>
        </div>
      </div>
    </form>
  )
}

export function mountProjectForm(el) {
  if (!el) return
  const clients = JSON.parse(el.dataset.clients || '[]')
  const users = JSON.parse(el.dataset.users || '[]')
  const milestoneStatuses = JSON.parse(el.dataset.milestoneStatuses || '[]')
  const errors = JSON.parse(el.dataset.errors || '[]')
  const formAction = el.dataset.formAction

  const root = createRoot(el)
  root.render(
    <ProjectForm
      formAction={formAction}
      clients={clients}
      users={users}
      milestoneStatuses={milestoneStatuses}
      initialErrors={errors}
    />
  )
}
